import { config } from '../../config';
import { StorageError } from '../../app/errors';
import type { SetPieceMarker, SetPieceRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `setPieces` — SP-1 … SP-6, and the first place INV-12 is either made achievable or lost.
 *
 * ── Geometry is rounded here, not in the serializer ──────────────────────────
 *
 * §11.7's determinism controls require "no floats in serialized geometry beyond 4 dp", because float
 * formatting varies between engines and one differing digit defeats INV-12's byte-identity. Rounding
 * at the **repo boundary** is what makes that reachable: U-5's serializer can only be
 * byte-deterministic if the bytes it is handed already are, and a value rounded at serialization time
 * would still differ from the value a *restored* profile holds, so an export → import → export
 * round-trip (BAK-2) would not be byte-stable.
 *
 * SP-5's markers are normalized 0–1 box coordinates, so `GEOMETRY_DECIMALS = 4` is about a tenth of a
 * millimetre on a penalty-box diagram — far below what a fingertip can express, and far above what
 * the share card can render.
 *
 * ── The draft row ────────────────────────────────────────────────────────────
 *
 * §9: *"The draft row is written on every marker tap (a small, non-hot-path transaction) so SES-3
 * resumes into a half-finished capture with the markers intact."* `upsertDraft` is that write, and
 * it is an upsert because the same capture is written many times as markers are dropped.
 */

/**
 * Writes or rewrites a set-piece capture.
 *
 * @throws {StorageError} outside a transaction, on out-of-range geometry, on a non-integer minute,
 *   or on a write failure.
 */
export async function upsertSetPiece(row: SetPieceRow): Promise<void> {
  requireTransaction('upsertSetPiece');
  if (!Number.isInteger(row.matchMinute) || row.matchMinute < 0) {
    throw new StorageError(
      `SP-4: a set-piece capture needs a finite non-negative integer matchMinute; ${row.id} carried ${String(row.matchMinute)}.`,
      { context: { store: TABLE.setPieces, id: row.id, matchMinute: row.matchMinute } },
    );
  }
  const normalized: SetPieceRow = { ...row, markers: row.markers.map(normalizeMarker) };
  try {
    await db().setPieces.put(normalized);
  } catch (cause) {
    throw new StorageError('Could not save the set-piece capture.', {
      cause,
      context: { store: TABLE.setPieces, id: row.id, matchId: row.matchId },
    });
  }
}

/**
 * SES-3 — clears the draft flag when the sheet is exited.
 *
 * Its own function rather than a general patch: `draft` is the one field whose meaning is "this
 * capture is unfinished", and a generic update that happened to carry `draft: true` from a stale
 * copy would resurrect a finished capture into the resume path.
 *
 * @throws {StorageError} outside a transaction, if the capture is missing, or on a write failure.
 */
export async function clearDraft(id: string): Promise<void> {
  requireTransaction('clearDraft');
  const row = await db().setPieces.get(id);
  if (!row) {
    throw new StorageError(`Set piece ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.setPieces, id },
    });
  }
  if (!row.draft) return;
  try {
    await db().setPieces.put({ ...row, draft: false });
  } catch (cause) {
    throw new StorageError('Could not close the set-piece capture.', {
      cause,
      context: { store: TABLE.setPieces, id },
    });
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteSetPiecesForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteSetPiecesForCascade');
  if (ids.length === 0) return;
  try {
    await db().setPieces.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the set-piece captures during a cascade.', {
      cause,
      context: { store: TABLE.setPieces, count: ids.length },
    });
  }
}

/** In `[matchId+matchMinute]` order — the order SP-4's per-match list reads. @throws {StorageError} */
export async function listForMatch(matchId: string): Promise<SetPieceRow[]> {
  try {
    return await db()
      .setPieces.where('[matchId+matchMinute]')
      .between([matchId, -Infinity], [matchId, Infinity])
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the match’s set pieces.', {
      cause,
      context: { store: TABLE.setPieces, matchId },
    });
  }
}

/**
 * SES-3's resume input: captures the user left half-finished.
 *
 * Filtered in memory from the match's rows — `draft` is a boolean, and IndexedDB does not accept a
 * boolean as a key, so an index on it would be accepted and hold no entries at all (build unit U-1
 * found this on `audioSegments.complete`). A match holds a handful of set pieces, so this is a scan
 * over a handful of rows.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listDrafts(matchId: string): Promise<SetPieceRow[]> {
  return (await listForMatch(matchId)).filter((row) => row.draft);
}

/** @throws {StorageError} on a read failure. */
export async function getSetPiece(id: string): Promise<SetPieceRow | undefined> {
  try {
    return await db().setPieces.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the set-piece capture.', {
      cause,
      context: { store: TABLE.setPieces, id },
    });
  }
}

/**
 * SP-5 — clamps to the 0–1 box and rounds to `GEOMETRY_DECIMALS`.
 *
 * Clamping as well as rounding, because a pointer can land a pixel outside the diagram's bounds and
 * a marker at `-0.0003` would render off the share card rather than on its edge.
 */
function normalizeMarker(marker: SetPieceMarker): SetPieceMarker {
  return { ...marker, x: round01(marker.x), y: round01(marker.y) };
}

function round01(value: number): number {
  if (!Number.isFinite(value)) {
    throw new StorageError(`SP-5: marker coordinates must be finite; got ${String(value)}.`, {
      context: { store: TABLE.setPieces, value },
    });
  }
  const factor = 10 ** config.GEOMETRY_DECIMALS;
  return Math.round(Math.min(1, Math.max(0, value)) * factor) / factor;
}
